import type { UpdatePatientRequest } from "@louise/medical-history/basic-info/types";
import type { SubstancesState } from "./SubstancesContext";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";

export function transformSubstancesStateToRequest(
  state: SubstancesState,
): RequestBody<paths["/{tenantId}/patients/{patientId}"]["patch"]> {
  const addictionsPayload: NonNullable<
    NonNullable<UpdatePatientRequest["lifestyle"]>["addictions"]
  > = [];

  // Create set of IDs to delete for quick lookup (prevents double-sending)
  const idsToDeleteSet = new Set(state.substanceIdsToDelete || []);

  // Add deletions from substanceIdsToDelete
  state.substanceIdsToDelete.forEach((id) => {
    if (id) {
      addictionsPayload.push({ id, op: "delete" });
    }
  });

  // If user answered "No" to having substances, delete all existing substances
  // (those with IDs that haven't already been marked for deletion)
  if (state.hasSubstances === false) {
    state.substanceDetails.forEach((detail) => {
      if (detail.id && !idsToDeleteSet.has(detail.id)) {
        addictionsPayload.push({ id: detail.id, op: "delete" });
      }
    });
    // Return early with delete operations only and hasAddictions flag
    const lifestylePayload: NonNullable<UpdatePatientRequest["lifestyle"]> = {
      hasAddictions: false,
    };
    if (addictionsPayload.length > 0) {
      lifestylePayload.addictions = addictionsPayload;
    }
    return {
      lifestyle: lifestylePayload,
    };
  }

  // Add substances to keep (excluding those marked for deletion)
  state.substanceDetails
    .filter((detail) => !detail.id || !idsToDeleteSet.has(detail.id))
    .forEach((detail) => {
    const entry: NonNullable<(typeof addictionsPayload)[number]> = {};

    if (detail.id) entry.id = detail.id;
    if (detail.substance != null) entry.substance = detail.substance;
    if (detail.frequency != null) entry.frequency = detail.frequency;
    if (typeof detail.averageQuantity === "number") {
      entry.averageQuantity = detail.averageQuantity;
    }
    if (detail.otherFrequency != null && detail.otherFrequency !== "") {
      entry.otherFrequency = detail.otherFrequency;
    }
    if (detail.startDate != null && detail.startDate !== "") {
      entry.startDate = detail.startDate;
    }
    if (detail.stopDate != null && detail.stopDate !== "") {
      entry.stopDate = detail.stopDate;
    }
    if (detail.notes != null && detail.notes !== "") {
      entry.notes = detail.notes;
    }
    if (detail.other != null && detail.other !== "") {
      entry.other = detail.other;
    }

    addictionsPayload.push(entry);
  });

  const lifestylePayload: NonNullable<UpdatePatientRequest["lifestyle"]> = {};

  if (state.hasSubstances !== undefined) {
    lifestylePayload.hasAddictions = state.hasSubstances ?? null;
  }

  if (addictionsPayload.length > 0) {
    lifestylePayload.addictions = addictionsPayload;
  }

  if (Object.keys(lifestylePayload).length === 0) {
    return {};
  }

  return {
    lifestyle: lifestylePayload,
  };
}
